import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Chip, Tooltip, Typography } from '@mui/material';

import use123zapStore from '../../../../../context/123zapStore';
import { notify } from '../../../../../shared/utils/utils';
import {
  INITIAL_COLUMNS,
  MODE_GRAPHIC_ROWS_PER_PAGE,
  SENT_MESSAGES_COLUMN_ORDER,
} from '../constants/modeGraphicModalConstants';
import {
  mapSentMessagesRows,
  mapSummaryWithTotal,
} from '../mappers/modeGraphicModalMappers';
import {
  downloadCampaignSummary,
  getCampaignSummary,
  getSentMessagesLines,
} from '../services/modeGraphicModalApi';

function buildStatusCell(params) {
  const statusStyles = {
    'entrega não confirmada': {
      color: 'warning',
      backgroundColor: '#fff3cd',
      tooltipMessage: 'O sistema do WhatsApp aceitou sua mensagem, mas ainda não confirmou a entrega ao destinatário.',
    },
    espera: {
      color: 'info',
      backgroundColor: '#e1f7ff',
      tooltipMessage: 'Sua mensagem foi criada e está aguardando envio.',
    },
    falha: {
      color: 'error',
      backgroundColor: '#f8d7da',
      tooltipMessage: 'Ocorreu uma falha no envio.',
    },
    entregue: {
      color: 'primary',
      backgroundColor: '#d1ecf1',
      tooltipMessage: 'Sua mensagem já foi entregue ao destinatário.',
    },
    lida: {
      color: 'success',
      backgroundColor: '#E9F5E3',
      tooltipMessage: 'O destinatário visualizou sua mensagem.',
    },
    desconhecido: {
      color: 'default',
      backgroundColor: '#f1f1f1',
      tooltipMessage: 'Não foi possível gerar o item. Tente novamente mais tarde.',
    },
    default: {
      color: 'default',
      backgroundColor: '#f1f1f1',
      tooltipMessage: 'Status desconhecido.',
    },
  };

  const value = params.value || 'desconhecido';
  const status = statusStyles[value.toLowerCase()] || statusStyles.default;

  return (
    <Tooltip
      title={
        <Typography sx={{ fontSize: '14px', alignContent: 'center', textAlign: 'center' }}>
          {status.tooltipMessage}
        </Typography>
      }
      arrow
    >
      <Chip
        label={value}
        color={status.color}
        variant="outlined"
        sx={{
          fontWeight: 'bold',
          borderRadius: '8px',
          backgroundColor: status.backgroundColor,
          textTransform: 'capitalize',
          minWidth: 100,
          padding: '4px 8px',
          cursor: 'pointer',
        }}
      />
    </Tooltip>
  );
}

function buildExcelColumns(columnOrder) {
  return columnOrder
    .filter((key) => key !== 'id' && key !== 'Mensagem de erro')
    .sort((a, b) => {
      const indexA = columnOrder.indexOf(a);
      const indexB = columnOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .map((key) => {
      const normalizeHeader = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const normalizedKey = normalizeHeader(key);

      if (normalizedKey === 'Ultimo Status') {
        return {
          field: key,
          headerName: key,
          flex: 1,
          minWidth: 180,
          renderCell: buildStatusCell,
        };
      }

      if (normalizedKey === 'Codigo de erro') {
        return {
          field: key,
          headerName: key,
          flex: 1,
          minWidth: 160,
          renderCell: (params) => {
            const value = params.value || 'Nenhum';

            return (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: '14px', alignContent: 'center', textAlign: 'center' }}>
                    {params.row['Mensagem de erro'] || 'Sem mensagem de erro'}
                  </Typography>
                }
                arrow
              >
                <Chip
                  label={value}
                  color={value !== 'Nenhum' ? 'error' : 'default'}
                  variant="outlined"
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    backgroundColor: value !== 'Nenhum' ? '#f8d7da' : '#f1f1f1',
                    textTransform: 'capitalize',
                    minWidth: 100,
                    padding: '4px 8px',
                    cursor: value !== 'Nenhum' ? 'pointer' : 'default',
                  }}
                />
              </Tooltip>
            );
          },
        };
      }

      return { field: key, headerName: key, flex: 1, minWidth: 150 };
    });
}

function getSafeMonthEndDate(monthStart) {
  const today = dayjs();
  const monthEnd = monthStart.endOf('month');

  return monthEnd.isAfter(today, 'day') ? today : monthEnd;
}

function getExecutionDateFromRow(row) {
  if (!row || typeof row !== 'object') return null;

  const executionDateKey = Object.keys(row).find(
    (key) => typeof key === 'string' && key.trim().toLowerCase() === 'data execução',
  );

  const rawValue = executionDateKey ? row[executionDateKey] : row?.['Data Execução'];
  if (typeof rawValue !== 'string') return null;

  const trimmedValue = rawValue.trim();
  if (!trimmedValue) return null;

  const ddmmyyyyMatch = trimmedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (ddmmyyyyMatch) {
    const [, day, month, year] = ddmmyyyyMatch;
    const parsed = dayjs(`${year}-${month}-${day}`);
    return parsed.isValid() ? parsed : null;
  }

  const parsed = dayjs(trimmedValue);
  return parsed.isValid() ? parsed : null;
}

export default function useModeGraphicModalController({ open, data, setLoading }) {
  const setTableColumns = use123zapStore((state) => state.setTableColumns);
  const setCampaignsSummaryData = use123zapStore((state) => state.setCampaignsSummaryData);
  const setExcelTableColumns = use123zapStore((state) => state.setExcelTableColumns);
  const setExcelTableRows = use123zapStore((state) => state.setExcelTableRows);
  const setShowNewTable = use123zapStore((state) => state.setShowNewTable);

  const tableColumns = use123zapStore((state) => state.tableColumns);
  const campaignsSummaryData = use123zapStore((state) => state.campaignsSummaryData);
  const excelTableRows = use123zapStore((state) => state.excelTableRows);
  const excelTableColumns = use123zapStore((state) => state.excelTableColumns);
  const showNewTable = use123zapStore((state) => state.showNewTable);

  const [loadingTable, setLoadingTable] = useState(true);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [loadingLabels, setLoadingLabels] = useState(true);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [sentMessagesLength, setSentMessagesLength] = useState(0);
  const [totalApiPages, setTotalApiPages] = useState(1);
  const [dynamicTableCampaignName, setDynamicTableCampaignName] = useState('');
  const dynamicRequestIdRef = useRef(0);

  const loadCampaignSummary = useCallback(
    async (customStart, customEnd) => {
      const body = {
        start_date: customStart.format('DD/MM/YYYY'),
        end_date: customEnd.format('DD/MM/YYYY'),
        refresh: true,
      };

      const items = await getCampaignSummary(body);
      const dataWithTotal = mapSummaryWithTotal(items.data);

      setTableColumns(Object.keys(dataWithTotal[0] || {}));
      setCampaignsSummaryData(dataWithTotal || {});
      setTotalItems(items.total_items);
    },
    [setCampaignsSummaryData, setTableColumns],
  );

  useEffect(() => {
    if (data?.date) {
      const [year, month] = data.date.split('-');
      const firstDay = dayjs(`${year}-${month}-01`);
      const lastDay = getSafeMonthEndDate(firstDay);
      setStartDate(firstDay);
      setEndDate(lastDay);
    }
  }, [data]);

  useEffect(() => {
    if (
      open &&
      data.date !== '' &&
      campaignsSummaryData.length === 0 &&
      excelTableColumns.length === 0 &&
      excelTableRows.length === 0
    ) {
      (async () => {
        setLoading(true);
        setLoadingSearch(true);
        setLoadingLabels(true);
        setLoadingTable(true);

        const summaryStartDate = dayjs(`${data.date}-01`);
        const summaryEndDate = getSafeMonthEndDate(summaryStartDate);

        setTableColumns(INITIAL_COLUMNS);

        try {
          await loadCampaignSummary(summaryStartDate, summaryEndDate);
        } catch (error) {
          console.error('Erro ao buscar os indicadores:', error);
        } finally {
          setLoading(false);
          setLoadingSearch(false);
          setLoadingLabels(false);
          setLoadingTable(false);
        }
      })();
    }
    // Mantém comportamento original de bootstrap do modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data, page]);

  const downloadExcel = async () => {
    setLoadingExcel(true);
    notify('Normal', 'Seu relatório está sendo baixado', true);

    const body = {
      filter_status: null,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      name_campaign: '',
    };

    try {
      const response = await downloadCampaignSummary(body);
      const urlDownload = response.data.url;
      const link = document.createElement('a');
      link.href = urlDownload;
      link.download = urlDownload.split('/').pop();
      link.click();
      notify('Success', 'Seu Relatório foi baixado com sucesso!', true);
    } catch (error) {
      console.error('Erro ao buscar os indicadores:', error);
      notify('Error', 'Ocorreu algum erro no download', true);
    } finally {
      setLoadingExcel(false);
    }
  };

  const searchCampaigns = async () => {
    setLoadingSearch(true);
    setLoadingLabels(true);
    setLoading(true);

    try {
      await loadCampaignSummary(startDate, endDate);
    } catch (error) {
      console.error('Erro ao buscar os indicadores:', error);
    } finally {
      setLoading(false);
      setLoadingSearch(false);
      setLoadingLabels(false);
    }
  };

  const fetchDataToNewTable = async ({
    campaignName = '',
    customStartDate = startDate,
    customEndDate = endDate,
  } = {}) => {
    const requestId = dynamicRequestIdRef.current + 1;
    dynamicRequestIdRef.current = requestId;
    const normalizedCampaignName = (campaignName || '').trim();

    setShowNewTable(true);
    setLoadingTable(true);
    setDynamicTableCampaignName(normalizedCampaignName);
    setExcelTableRows([]);
    setExcelTableColumns([]);
    setSentMessagesLength(0);
    setTotalApiPages(1);

    const body = {
      start_date: dayjs(customStartDate).format('DD/MM/YYYY'),
      end_date: dayjs(customEndDate).format('DD/MM/YYYY'),
      refresh: true,
      page: 1,
      name_campaign: normalizedCampaignName,
    };

    try {
      const response = await getSentMessagesLines(body);
      if (requestId !== dynamicRequestIdRef.current) return;
      const formattedData = mapSentMessagesRows(response.data.data, body.page);

      setSentMessagesLength(response.data.total_items);
      setTotalApiPages(response.data.total_pages);
      setExcelTableRows(formattedData);

      if (formattedData.length > 0) {
        setExcelTableColumns(buildExcelColumns(SENT_MESSAGES_COLUMN_ORDER));
      }
    } catch (error) {
      console.error('Erro ao buscar os indicadores:', error);
    } finally {
      if (requestId === dynamicRequestIdRef.current) {
        setLoadingTable(false);
      }
    }
  };

  const openCampaignAnalysis = async (row) => {
    const campaignName = row?.Campanha;

    if (!campaignName || campaignName === 'Total') return;

    const executionDate = getExecutionDateFromRow(row);
    if (!executionDate) return;

    setStartDate(executionDate);
    setEndDate(executionDate);

    await fetchDataToNewTable({
      campaignName,
      customStartDate: executionDate,
      customEndDate: executionDate,
    });
  };

  return {
    tableColumns,
    campaignsSummaryData,
    excelTableRows,
    excelTableColumns,
    showNewTable,
    loadingTable,
    setLoadingTable,
    page,
    setPage,
    totalItems,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    loadingLabels,
    loadingExcel,
    loadingSearch,
    sentMessagesLength,
    setSentMessagesLength,
    totalApiPages,
    setTotalApiPages,
    rowsPerPage: MODE_GRAPHIC_ROWS_PER_PAGE,
    downloadExcel,
    searchCampaigns,
    fetchDataToNewTable,
    openCampaignAnalysis,
    dynamicTableCampaignName,
    setShowNewTable,
    setExcelTableRows,
    setExcelTableColumns,
  };
}
